// Shared feature-flag registry — the single source of truth for both backend
// resolution (services/features.ts) and the super-admin UI. DB-backed overrides
// (feature_overrides table) layer on top of these code defaults.
export const FEATURE_PLANS = [
    'FREE',
    'ESSENTIALS',
    'PRO',
    'ENTERPRISE',
];
const allPlans = (value) => ({
    FREE: value,
    ESSENTIALS: value,
    PRO: value,
    ENTERPRISE: value,
});
export const FEATURES = [
    // ── Plan-tier features (Inherit = billing plan) ──
    {
        key: 'alerts',
        label: 'Alerts',
        description: 'Event-triggered Actions / notifications (Slack, email, Telegram, Discord).',
        kind: 'plan',
        defaults: { FREE: false, ESSENTIALS: true, PRO: true, ENTERPRISE: true },
    },
    {
        key: 'webhooks',
        label: 'Webhooks',
        description: 'Outbound webhook Actions.',
        kind: 'plan',
        defaults: { FREE: false, ESSENTIALS: false, PRO: true, ENTERPRISE: true },
    },
    {
        key: 'apiAccess',
        label: 'API Access',
        description: 'Programmatic reads with an API token or OAuth client (search, analytics, companies, revenue, identities) and embeddable dashboard tokens. Ingest, dashboards, and assistant (MCP) reads are free on every plan.',
        kind: 'plan',
        defaults: { FREE: false, ESSENTIALS: false, PRO: true, ENTERPRISE: true },
    },
    {
        key: 'companies',
        label: 'Companies',
        description: 'Account intelligence (companies & groups). Capped per plan.',
        kind: 'plan',
        defaults: allPlans(true),
    },
    // ── System launch gates (Inherit = global DB default; off until ready) ──
    {
        key: 'enrichment',
        label: 'Company Enrichment',
        description: 'Firmographic enrichment surfaces — Automations → Enrichment, Settings → Enrichment, and the company Enrich action.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'actionsCredits',
        label: 'Actions & Credits',
        description: 'The single Actions + Data Credits gate. Powers the Billing balance UI (Actions allowance & Data Credit wallet), top-ups, auto-refill, the extra-Actions add-on, and hard enforcement (HTTP 402 on shortfall). Off = surfaces hidden and usage is record-only (no block).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'actionsScheduler',
        label: 'Scheduled Actions',
        description: 'Runs saved actions on a schedule (the in-process scheduler drains due actions and runs each via runOnce). Off = the "On a Schedule" trigger stores config but never fires.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'demoBootstrap',
        label: 'Demo Bootstrap',
        description: 'Provision the synthetic demo org in this environment. OFF by default so no environment (prod included) auto-creates one on deploy. Turn the global override on once per environment to create it on the next boot (idempotent find-or-create, never deletes). Independent of Demo Top-Up: turning this back off never removes an existing demo.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'demoTopup',
        label: 'Demo Top-Up',
        description: 'Kill switch for the synthetic demo top-up (the in-process job that re-seeds the demo org rolling window so it stays fresh). ON by default; set the global override off to pause the daily refresh. Never deletes the demo org or its data. No effect in environments without a demo org.',
        kind: 'system',
        defaults: allPlans(true),
    },
    {
        key: 'targetAccounts',
        label: 'Target Accounts',
        description: 'Target Account insights (kind=account profiles): Save-from-Research, Promote-from-Company, the Target Accounts tab, and run-form prefill. Off = surfaces hidden; existing account profiles keep serving API reads.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'gtmSignals',
        label: 'GTM Signals',
        description: 'The signals layer: internal preset recipes evaluated on the event path, the signals feed + per-account timelines, and external signals emitted by Account Watch. Off = nothing is recorded and the feed is hidden.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'actionChaining',
        label: 'Signal-Triggered Actions & Chains',
        description: "Signal triggers on actions plus the one-hop 'action' destination (signal → agent action → notification). Off = signal triggers store config but never fire and chains never execute.",
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'visitorId',
        label: 'Visitor Identification',
        description: 'Master gate for anonymous visitor identification at ingest. Gates the per-capability flags — visitorIdCompany today, person-level to follow. Off = no anonymous session resolves to a company or person.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'visitorIdCompany',
        label: 'Company Visitor Identification',
        description: 'Company-level reverse-IP resolution: resolve an anonymous session IP to a company and set companyId, feeding the GTM signal recipes. Requires the visitorId master gate plus a configured visitor_company provider. Geographic scope is the org region policy (Company Visitor ID settings): explicitly-consented visitors resolve anywhere; without explicit consent the policy decides (default us_only). Off = anonymous traffic stays unresolved.',
        kind: 'system',
        parent: 'visitorId',
        defaults: allPlans(false),
    },
    {
        key: 'datalegion',
        label: 'Data Legion Provider',
        description: 'Ops kill-switch for the Data Legion enrichment provider (also requires its API key + endpoint to be configured).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'cleanlist',
        label: 'Cleanlist Provider',
        description: 'Ops kill-switch for the Cleanlist company-enrichment provider (also requires its API key to be configured).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'delivr',
        label: 'Delivr Provider',
        description: 'Ops kill-switch for the Delivr person-lookup provider (also requires its API key + secret to be configured).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'brave',
        label: 'Brave Search Provider',
        description: 'Ops kill-switch for the Brave Search web-search provider (also requires its API key to be configured).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'tavily',
        label: 'Tavily Search Provider',
        description: 'Ops kill-switch for the Tavily Search web-search provider (also requires its API key to be configured).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'dataforseo',
        label: 'DataForSEO Provider',
        description: 'Ops kill-switch for the DataForSEO web-search provider (the primary web_search leg; also requires its API login + password to be configured).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'webResearch',
        label: 'Web Research Provider',
        description: 'Ops kill-switch for the web-research company-enrichment provider (draws on the web-search waterfall plus an extraction model; also requires a routable web-search provider).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'autocapture',
        label: 'Autocapture',
        description: 'No-code per-project event autocapture (outbound links, file downloads, form submissions, video/audio engagement) + its Project Settings surface.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'stripeApp',
        label: 'Stripe Integration',
        description: 'Connect a Stripe account on the Revenue settings page to auto-ingest payments. Separate from Revenue Attribution: off = the Revenue page shows the Stripe integration as "coming soon" (the rest of Revenue still works via the manual SDK). Pending Stripe app review.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'searchConsole',
        label: 'Search Console',
        description: 'Google Search Console reporting joined to company identity: the Analytics → Search page and the Project Settings → Search Console connect flow. Off = both surfaces are hidden and the report/connect endpoints 403; an existing connection stays vaulted and resumes when the flag returns.',
        kind: 'system',
        defaults: allPlans(false),
    },
    // ── AI launch gates (Inherit = global DB default; off until ready) ──
    {
        key: 'ai',
        label: 'AI',
        description: 'Master AI gate — the Bedrock gateway, AI Settings page, and any AI-powered action.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'aiWeeklySummary',
        label: 'AI Weekly Summary',
        description: 'Weekly data summary action (deterministic template + optional AI narrative).',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'companyProfile',
        label: 'Company Profile (Profile Agent + ICP Analyst)',
        description: 'Build Company Profile (profile.build): the Profile Agent researches the org GTM Company Profile from its website via internal enrichment + web search, and the ICP Analyst sharpens the ICP from real accounts + events (they work together; the Analyst skips itself without data).',
        kind: 'system',
        parent: 'ai',
        defaults: allPlans(false),
    },
    {
        key: 'prospectResearch',
        label: 'Prospect Researcher',
        description: 'Prospect Researcher (prospect.research): parallel company + contact brief tuned to the org profile, via web search.',
        kind: 'system',
        parent: 'ai',
        defaults: allPlans(false),
    },
    {
        key: 'leadQualify',
        label: 'Lead Qualifier',
        description: 'Lead Qualifier (lead.qualify): deterministic 0–100 fit score vs the org profile ICP + personas.',
        kind: 'system',
        parent: 'ai',
        defaults: allPlans(false),
    },
    {
        key: 'outreachDraft',
        label: 'Outreach Drafter',
        description: 'Outreach Drafter (outreach.draft): cold email + LinkedIn sequence from a qualified prospect.',
        kind: 'system',
        parent: 'ai',
        defaults: allPlans(false),
    },
    {
        key: 'byok',
        label: 'Bring Your Own Key (BYOK)',
        description: 'Per-org LLM provider key management + BYOK routing for AI actions.',
        kind: 'system',
        defaults: allPlans(false),
    },
    {
        key: 'personLookup',
        label: 'Person Lookup',
        description: 'Person (people) enrichment lookup action.',
        kind: 'system',
        defaults: allPlans(false),
    },
];
export const FEATURE_KEYS = FEATURES.map((f) => f.key);
export const FEATURE_BY_KEY = Object.fromEntries(FEATURES.map((f) => [f.key, f]));
export const isFeatureKey = (key) => key in FEATURE_BY_KEY;
//# sourceMappingURL=features.js.map